import type { RotationMediaPoolItemDto } from '@carvia/ros-client-types';
import { buildWeightedDeck, findNextInDeck } from '@/utils/build-weighted-deck';
import { weightedPick } from '@/utils/weighted-pick';
import { computed, ref, type Ref } from 'vue';

type TakeNextOptions = {
  failedMediaIds?: string[];
  avoidMediaId?: string | null;
  allowRepeat?: boolean;
};

export function useRotationDeck(pool: Ref<RotationMediaPoolItemDto[]>) {
  const deck = ref<RotationMediaPoolItemDto[]>([]);
  const deckIndex = ref(0);
  const pendingRebuildFailedMediaIds = ref<string[] | null>(null);
  const hasPendingRebuild = computed(() => pendingRebuildFailedMediaIds.value !== null);

  function getExcludeIds({ failedMediaIds = [], avoidMediaId, allowRepeat }: TakeNextOptions) {
    const exclude = [...failedMediaIds];
    if (!allowRepeat && avoidMediaId) {
      exclude.push(avoidMediaId);
    }
    return exclude;
  }

  function rebuildDeck(failedMediaIds: string[] = []) {
    pendingRebuildFailedMediaIds.value = null;
    const failedSet = new Set(failedMediaIds);
    const playable = pool.value.filter((item) => item.weight > 0 && !failedSet.has(item.mediaId));
    deck.value = buildWeightedDeck(playable);
    deckIndex.value = 0;
  }

  function scheduleRebuildDeck(failedMediaIds: string[] = []) {
    pendingRebuildFailedMediaIds.value = failedMediaIds;
  }

  function consumePendingRebuild() {
    if (pendingRebuildFailedMediaIds.value === null) return;
    rebuildDeck(pendingRebuildFailedMediaIds.value);
  }

  function syncDeckFromPool() {
    if (!deck.value.length) {
      rebuildDeck();
      return;
    }

    const poolByMediaId = new Map(pool.value.map((item) => [item.mediaId, item]));
    deck.value = deck.value.map((item) => poolByMediaId.get(item.mediaId) ?? item);
  }

  function peekNext(options: TakeNextOptions = {}): RotationMediaPoolItemDto | null {
    const exclude = getExcludeIds(options);

    if (!deck.value.length) {
      rebuildDeck(options.failedMediaIds ?? []);
    }

    const match = findNextInDeck(deck.value, deckIndex.value, exclude);
    return match?.item ?? null;
  }

  function takeNext(options: TakeNextOptions = {}): RotationMediaPoolItemDto | null {
    const failedMediaIds = options.failedMediaIds ?? [];
    const exclude = getExcludeIds(options);

    if (!deck.value.length) {
      rebuildDeck(failedMediaIds);
    }

    let match = findNextInDeck(deck.value, deckIndex.value, exclude);

    if (!match && options.allowRepeat) {
      match = findNextInDeck(deck.value, deckIndex.value, failedMediaIds);
    }

    if (!match) {
      return weightedPick(
        pool.value.filter((item) => item.weight > 0),
        exclude,
      );
    }

    deckIndex.value = match.index + 1;
    if (deckIndex.value >= deck.value.length) {
      deckIndex.value = 0;
      rebuildDeck(failedMediaIds);
    }

    return match.item;
  }

  return {
    deck,
    deckIndex,
    hasPendingRebuild,
    rebuildDeck,
    scheduleRebuildDeck,
    consumePendingRebuild,
    syncDeckFromPool,
    peekNext,
    takeNext,
  };
}
